/**
 * PixiWorldRenderer.js - WebGL World Rendering
 *
 * Handles rendering of world elements using PixiJS:
 * - Tiled tile layers (ground/floor)
 * - Background grid
 * - Map boundary walls
 * - World objects with rotation
 */

import * as PIXI from 'pixi.js';
import { ClientLogger } from '../../logger/ClientLogger.js';
import { drawFlatObject, getWorldTheme } from './worldTheme.js';
import { zoneBounds } from './hud/minimapMath.js';

const pixiWorldLoggerFallback = (() => {
  if (typeof window !== 'undefined' && window.console) {
    return window.console;
  }
  return { log: () => {}, warn: () => {}, error: () => {}, info: () => {} };
})();
const pixiWorldLogger = ClientLogger.getLogger('renderer') || pixiWorldLoggerFallback;

const PixiWorldRenderer = {
  // Layer containers
  gridContainer: null,
  wallsContainer: null,
  objectsContainer: null,
  tilesContainer: null, // For Tiled tile layers
  debugContainer: null, // For debug collision visualization

  // Grid configuration
  gridSize: 50,
  gridColor: 0xc8c8c8,
  gridAlpha: 0.15,

  // Wall configuration
  wallThickness: 100,
  wallColor: 0x222222,
  wallBorderColor: 0x444444,

  // Cached graphics
  groundGraphics: null, // playable-arena fill (tiles layer, under the grid)
  gridGraphics: null,
  wallsGraphics: null,
  shapesGraphics: null, // flat (image-less) world objects, drawn from worldTheme
  _shapesKey: '',
  debugGraphics: null, // Debug collision graphics
  zoneOverlayContainer: null,
  zoneOverlayGraphics: null,
  zoneBorderGraphics: null,

  // World object sprites (Map: objectId -> PIXI.Sprite)
  objectSprites: new Map(),

  // Tiled map data
  tiledData: null,
  tileSprites: [], // Array of tile sprites
  tileTextures: new Map(), // imagePath -> PIXI.Texture
  tilesLoaded: false,
  tilesCreated: false,

  // Last known camera position for grid parallax
  lastCameraX: 0,
  lastCameraY: 0,

  // Debug mode
  debugCollisions: false,

  /**
   * Initialize the world renderer
   * @param {PIXI.Container} gridContainer - Container for grid (screen space)
   * @param {PIXI.Container} wallsContainer - Container for walls (world space)
   * @param {PIXI.Container} objectsContainer - Container for world objects
   * @param {PIXI.Container} tilesContainer - Container for tile layers (optional)
   * @param {PIXI.Container} debugContainer - Container for debug graphics (optional)
   */
  init(gridContainer, wallsContainer, objectsContainer, tilesContainer = null, debugContainer = null, zoneOverlayContainer = null) {
    this.gridContainer = gridContainer;
    this.wallsContainer = wallsContainer;
    this.objectsContainer = objectsContainer;
    this.tilesContainer = tilesContainer || wallsContainer; // Use walls container if no dedicated tiles container
    this.debugContainer = debugContainer || objectsContainer; // Use objects container if no dedicated debug container
    this.zoneOverlayContainer = zoneOverlayContainer || objectsContainer;

    // Create graphics objects
    this.groundGraphics = new PIXI.Graphics();
    this.tilesContainer.addChildAt(this.groundGraphics, 0);
    this.gridGraphics = new PIXI.Graphics();
    
    // Create debug graphics
    this.debugGraphics = new PIXI.Graphics();
    this.debugContainer.addChild(this.debugGraphics);
    this.gridContainer.addChild(this.gridGraphics);

    this.wallsGraphics = new PIXI.Graphics();
    this.wallsContainer.addChild(this.wallsGraphics);

    this.shapesGraphics = new PIXI.Graphics();
    this.objectsContainer.addChild(this.shapesGraphics);

    this.zoneOverlayGraphics = new PIXI.Graphics();
    this.zoneBorderGraphics = new PIXI.Graphics();
    this.zoneOverlayContainer.addChild(this.zoneOverlayGraphics);
    this.zoneOverlayContainer.addChild(this.zoneBorderGraphics);
  },

  /**
   * Set Tiled map data for rendering
   * @param {Object} tiledData - Loaded Tiled map data from TiledMapLoader
   */
  setTiledData(tiledData) {
    // Clear old map data first
    this.clearMapData();
    
    this.tiledData = tiledData;
    this.tilesLoaded = false;
    this.tilesCreated = false;
    this._loadTileTextures();
  },

  /**
   * Clear all map-related graphics and sprites
   */
  clearMapData() {
    // Clear tile sprites
    for (const sprite of this.tileSprites) {
      if (sprite.parent) {
        sprite.parent.removeChild(sprite);
      }
      sprite.destroy();
    }
    this.tileSprites = [];

    // Clear object sprites
    for (const [objId, sprite] of this.objectSprites.entries()) {
      if (sprite.parent) {
        sprite.parent.removeChild(sprite);
      }
      sprite.destroy();
    }
    this.objectSprites.clear();

    // Clear graphics
    if (this.groundGraphics) {
      this.groundGraphics.clear();
    }
    if (this.wallsGraphics) {
      this.wallsGraphics.clear();
    }
    if (this.shapesGraphics) {
      this.shapesGraphics.clear();
      this._shapesKey = '';
    }
    if (this.gridGraphics) {
      this.gridGraphics.clear();
    }
    if (this.debugGraphics) {
      this.debugGraphics.clear();
    }

    // Reset state
    this.tilesCreated = false;
    this.tilesLoaded = false;
  },

  /**
   * Load all tile textures needed for rendering
   * @private
   */
  async _loadTileTextures() {
    if (!this.tiledData) return;

    const imagesToLoad = new Set();

    // Collect all unique image paths from tilesets
    for (const tileset of this.tiledData.tilesets) {
      if (tileset.image) {
        imagesToLoad.add(tileset.image);
      }
      // Collection tilesets have individual tile images
      if (tileset.tiles) {
        for (const [gid, tile] of tileset.tiles) {
          if (tile.image) {
            imagesToLoad.add(tile.image);
          }
        }
      }
    }

    const totalImages = imagesToLoad.size;

    if (totalImages === 0) {
      this.tilesLoaded = true;
      this._createTileSprites();
      return;
    }

    // Load all textures using PixiJS Assets API (PixiJS 8.x)
    const loadPromises = [];
    
    for (const imagePath of imagesToLoad) {
      if (this.tileTextures.has(imagePath)) {
        continue;
      }

      // Use PIXI.Assets for proper async loading in PixiJS 8.x
      const loadPromise = PIXI.Assets.load(imagePath)
        .then((texture) => {
          this.tileTextures.set(imagePath, texture);
        })
        .catch((err) => {
          pixiWorldLogger.error(`[PixiWorldRenderer] Failed to load tile texture: ${imagePath}`, err);
        });
      
      loadPromises.push(loadPromise);
    }

    // Wait for all textures to load
    await Promise.all(loadPromises);
    
    this.tilesLoaded = true;
    this._createTileSprites();
  },

  /**
   * Create tile sprites for all tile layers
   * @private
   */
  _createTileSprites() {
    if (this.tilesCreated || !this.tiledData || !this.tilesLoaded) return;

    // Clear existing tile sprites
    for (const sprite of this.tileSprites) {
      if (sprite.parent) {
        sprite.parent.removeChild(sprite);
      }
      sprite.destroy();
    }
    this.tileSprites = [];

    // Create sprites for each tile layer
    for (const layer of this.tiledData.tileLayers) {
      if (!layer.visible) continue;

      for (const tile of layer.tiles) {
        const baseTexture = this.tileTextures.get(tile.image);
        if (!baseTexture) {
          pixiWorldLogger.warn(`[PixiWorldRenderer] Missing texture for: ${tile.image}`);
          continue;
        }

        // Create sprite - for tilesheets we need to use a sub-region
        let sprite;
        
        if (tile.srcWidth && tile.srcHeight && (tile.srcX !== undefined || tile.srcY !== undefined)) {
          // Tilesheet - extract region using Spritesheet-like approach
          // In PixiJS 8.x, we create a new texture with frame from the source
          try {
            const frame = new PIXI.Rectangle(
              tile.srcX || 0, 
              tile.srcY || 0, 
              tile.srcWidth, 
              tile.srcHeight
            );
            
            // Create a new texture from the same source with a different frame
            const texture = new PIXI.Texture({
              source: baseTexture.source,
              frame: frame
            });
            
            sprite = new PIXI.Sprite(texture);
          } catch (e) {
            pixiWorldLogger.error(`[PixiWorldRenderer] Error creating tilesheet sprite:`, e);
            sprite = new PIXI.Sprite(baseTexture);
          }
        } else {
          // Individual tile image - use entire texture
          sprite = new PIXI.Sprite(baseTexture);
        }

        sprite.x = tile.x;
        sprite.y = tile.y;
        sprite.width = tile.width;
        sprite.height = tile.height;
        sprite.alpha = layer.opacity;

        this.tilesContainer.addChild(sprite);
        this.tileSprites.push(sprite);
      }
    }

    this.tilesCreated = true;
  },

  /**
   * Draw the background grid in world space
   * Grid covers the visible area plus margins for smooth scrolling
   * @param {number} cameraX - Camera X position (center of view)
   * @param {number} cameraY - Camera Y position (center of view)
   * @param {number} screenWidth - Screen width
   * @param {number} screenHeight - Screen height
   */
  drawGrid(cameraX, cameraY, screenWidth, screenHeight) {
    const g = this.gridGraphics;
    g.clear();

    // Calculate world-space bounds of visible area (with margin)
    const margin = this.gridSize * 2;
    let left = cameraX - screenWidth / 2 - margin;
    let right = cameraX + screenWidth / 2 + margin;
    let top = cameraY - screenHeight / 2 - margin;
    let bottom = cameraY + screenHeight / 2 + margin;

    // Keep the grid inside the playable arena so the darker out-of-bounds
    // grass stays a clean field.
    if (this._mapWidth > 0 && this._mapHeight > 0) {
      left = Math.max(0, left);
      top = Math.max(0, top);
      right = Math.min(this._mapWidth, right);
      bottom = Math.min(this._mapHeight, bottom);
      if (right <= left || bottom <= top) {
        this.lastCameraX = cameraX;
        this.lastCameraY = cameraY;
        return;
      }
    }

    // Snap to grid
    const startX = Math.floor(left / this.gridSize) * this.gridSize;
    const startY = Math.floor(top / this.gridSize) * this.gridSize;

    // PixiJS 8.x API - use stroke with path
    g.setStrokeStyle({ width: 1, color: this.gridColor, alpha: this.gridAlpha });

    // Draw vertical lines in world space
    for (let x = startX; x <= right; x += this.gridSize) {
      g.moveTo(x, top);
      g.lineTo(x, bottom);
    }

    // Draw horizontal lines in world space
    for (let y = startY; y <= bottom; y += this.gridSize) {
      g.moveTo(left, y);
      g.lineTo(right, y);
    }

    // Apply the stroke
    g.stroke();

    this.lastCameraX = cameraX;
    this.lastCameraY = cameraY;
  },

  /**
   * Draw map boundary walls
   * @param {number} mapWidth - Map width
   * @param {number} mapHeight - Map height
   */
  drawWalls(mapWidth, mapHeight) {
    this._mapWidth = mapWidth;
    this._mapHeight = mapHeight;

    if (this.groundGraphics) {
      this.groundGraphics.clear();
      this.groundGraphics.rect(0, 0, mapWidth, mapHeight);
      this.groundGraphics.fill({ color: getWorldTheme().ground });
    }

    const g = this.wallsGraphics;
    g.clear();

    const t = this.wallThickness;

    // PixiJS 8.x API - draw and fill walls
    // Walls extend outside the playable area to match physics collision
    // Each wall extends beyond corners to ensure no gaps
    // Top wall (extends above map and past corners)
    g.rect(-t, -t, mapWidth + t * 2, t);
    // Bottom wall (extends below map and past corners)
    g.rect(-t, mapHeight, mapWidth + t * 2, t);
    // Left wall (extends left of map, full height from -t to mapHeight+t to match physics)
    g.rect(-t, -t, t, mapHeight + t * 2);
    // Right wall (extends right of map, full height from -t to mapHeight+t to match physics)
    g.rect(mapWidth, -t, t, mapHeight + t * 2);

    g.fill({ color: this.wallColor });

    // Draw boundary indicator (dashed border at playable area edge)
    const dashLength = 5;
    const gapLength = 5;
    const innerX = 0;
    const innerY = 0;
    const innerWidth = mapWidth;
    const innerHeight = mapHeight;

    this._drawDashedRect(g, innerX, innerY, innerWidth, innerHeight, dashLength, gapLength);
  },

  /**
   * Draw a dashed rectangle
   * @private
   */
  _drawDashedRect(g, x, y, width, height, dashLen, gapLen) {
    // Set stroke style for dashed lines
    g.setStrokeStyle({ width: 2, color: this.wallBorderColor, alpha: 1 });

    // Top edge
    this._drawDashedLine(g, x, y, x + width, y, dashLen, gapLen);
    // Right edge
    this._drawDashedLine(g, x + width, y, x + width, y + height, dashLen, gapLen);
    // Bottom edge
    this._drawDashedLine(g, x + width, y + height, x, y + height, dashLen, gapLen);
    // Left edge
    this._drawDashedLine(g, x, y + height, x, y, dashLen, gapLen);

    // Apply stroke
    g.stroke();
  },

  /**
   * Draw a dashed line
   * @private
   */
  _drawDashedLine(g, x1, y1, x2, y2, dashLen, gapLen) {
    const dx = x2 - x1;
    const dy = y2 - y1;
    const length = Math.sqrt(dx * dx + dy * dy);
    const segments = length / (dashLen + gapLen);

    const ux = dx / length;
    const uy = dy / length;

    let currentX = x1;
    let currentY = y1;

    for (let i = 0; i < segments; i++) {
      g.moveTo(currentX, currentY);
      g.lineTo(currentX + ux * dashLen, currentY + uy * dashLen);
      currentX += ux * (dashLen + gapLen);
      currentY += uy * (dashLen + gapLen);
    }
  },

  /**
   * Update world objects with rotation support
   * @param {Map} worldObjects - Map of world object data
   * @param {Object} callbacks
   * @param {Function} callbacks.getRenderable - Returns render data ({type, image, frame})
   * @param {Function} callbacks.getTextureForStatic - Returns PIXI texture for static images
   * @param {Function} callbacks.getTextureForFrame - Returns PIXI texture for animation frames
   */
  updateWorldObjects(worldObjects, callbacks = {}) {
    const {
      getRenderable,
      getTextureForStatic,
      getTextureForFrame
    } = callbacks;

    // Track which objects still exist
    const existingIds = new Set();
    const flat = [];

    for (const obj of worldObjects.values()) {
      existingIds.add(obj.id);

      const renderable = getRenderable ? getRenderable(obj) : null;
      if (!renderable) {
        // No artwork: draw it as a themed flat shape (walls, blocks, pillars).
        // (Loot containers have their own renderer; everything else without art is a themed flat shape.)
        if (obj.objectType !== 'lootContainer' && obj.visible !== false && (obj.isPolygon || (obj.width && obj.height))) flat.push(obj);
        continue;
      }

      let sprite = this.objectSprites.get(obj.id);

      if (!sprite) {
        sprite = new PIXI.Sprite();
        sprite.anchor.set(0.5, 0.5);
        this.objectsContainer.addChild(sprite);
        this.objectSprites.set(obj.id, sprite);
      }

      let texture = null;
      if (renderable.type === 'animation' && getTextureForFrame) {
        texture = getTextureForFrame(renderable);
      } else if (renderable.image && getTextureForStatic) {
        texture = getTextureForStatic(renderable.image);
      }

      if (!texture) {
        continue;
      }

      sprite.texture = texture;
      // Update sprite position and properties
      sprite.x = obj.x;
      sprite.y = obj.y;
      sprite.width = obj.width || texture.width || sprite.width;
      sprite.height = obj.height || texture.height || sprite.height;
      sprite.alpha = obj.opacity !== undefined ? obj.opacity : 1.0;

      // Apply rotation (convert degrees to radians)
      if (obj.rotation && obj.rotation !== 0) {
        sprite.rotation = (obj.rotation * Math.PI) / 180;
      } else {
        sprite.rotation = 0;
      }
    }

    this._drawFlatObjects(flat);

    // Remove sprites for objects that no longer exist
    for (const [id, sprite] of this.objectSprites) {
      if (!existingIds.has(id)) {
        this.objectsContainer.removeChild(sprite);
        sprite.destroy();
        this.objectSprites.delete(id);
      }
    }
  },

  /**
   * Redraw the flat-shape layer, but only when the set of flat objects changed
   * (static geometry — a per-frame rebuild would be the HUD mistake all over again).
   * @param {Array} flat - world objects without artwork
   */
  _drawFlatObjects(flat) {
    const g = this.shapesGraphics;
    if (!g) return;
    let key = '';
    for (const o of flat) key += o.id + ':' + o.x + ',' + o.y + ':' + (o.hp ?? '') + ';';
    if (key === this._shapesKey) return;
    this._shapesKey = key;
    g.clear();
    for (const obj of flat) drawFlatObject(g, obj);
  },

  /**
   * Toggle debug collision visualization
   */
  toggleDebugCollisions() {
    this.debugCollisions = !this.debugCollisions;
    pixiWorldLogger.info(`[PixiWorldRenderer] Debug collisions: ${this.debugCollisions ? 'ON' : 'OFF'}`);
    if (!this.debugCollisions && this.debugGraphics) {
      this.debugGraphics.clear();
    }
  },

  /**
   * Draw debug collision boundaries for world objects
   * Shows rectangles in blue and polygons in red
   * @param {Map} worldObjects - Map of world object data
   */
  drawDebugCollisions(worldObjects) {
    if (!this.debugGraphics) return;
    
    this.debugGraphics.clear();
    
    if (!this.debugCollisions) return;

    for (const obj of worldObjects.values()) {
      // Draw polygon collision (red = visual polygon from Tiled)
      if (obj.isPolygon && obj.vertices && obj.vertices.length >= 3) {
        // Build polygon points array - vertices are relative to obj.x, obj.y
        const points = [];
        for (const v of obj.vertices) {
          points.push(obj.x + v.x, obj.y + v.y);
        }
        
        // Draw filled polygon (Tiled visual)
        this.debugGraphics.poly(points);
        this.debugGraphics.fill({ color: 0xff0000, alpha: 0.2 });
        this.debugGraphics.stroke({ color: 0xff0000, alpha: 0.9, width: 2 });

        // Draw anchor point (green circle)
        this.debugGraphics.circle(obj.x, obj.y, 5);
        this.debugGraphics.fill({ color: 0x00ff00, alpha: 0.8 });

        // Draw actual physics body vertices (cyan) if available
        if (obj.bodyVertices && obj.bodyVertices.length >= 3) {
          const bodyPoints = [];
          for (const v of obj.bodyVertices) {
            bodyPoints.push(v.x, v.y);
          }
          this.debugGraphics.poly(bodyPoints);
          this.debugGraphics.fill({ color: 0x00ffff, alpha: 0.15 });
          this.debugGraphics.stroke({ color: 0x00ffff, alpha: 0.9, width: 3 });
        }

        // Draw body center point (magenta circle) if available
        if (obj.bodyPosition) {
          this.debugGraphics.circle(obj.bodyPosition.x, obj.bodyPosition.y, 6);
          this.debugGraphics.fill({ color: 0xff00ff, alpha: 0.8 });
        }
      }

      // Draw rectangle collisions (blue)
      if (obj.collision && obj.collision.length > 0) {
        for (const coll of obj.collision) {
          // collision coords are relative to obj position (which is center)
          const worldX = obj.x + coll.x;
          const worldY = obj.y + coll.y;

          // Handle rotation
          if (obj.rotation && obj.rotation !== 0) {
            // Draw rotated rectangle using polygon
            const angle = (obj.rotation * Math.PI) / 180;
            const hw = coll.width / 2;
            const hh = coll.height / 2;
            const cos = Math.cos(angle);
            const sin = Math.sin(angle);
            
            const corners = [
              { x: -hw, y: -hh },
              { x: hw, y: -hh },
              { x: hw, y: hh },
              { x: -hw, y: hh }
            ];

            // Rotate and translate corners to points array
            const points = [];
            for (const c of corners) {
              points.push(
                worldX + c.x * cos - c.y * sin,
                worldY + c.x * sin + c.y * cos
              );
            }

            this.debugGraphics.poly(points);
            this.debugGraphics.fill({ color: 0x0064ff, alpha: 0.2 });
            this.debugGraphics.stroke({ color: 0x0064ff, alpha: 0.9, width: 2 });
          } else {
            // collision x,y is center of collision box
            const drawX = worldX - coll.width / 2;
            const drawY = worldY - coll.height / 2;
            this.debugGraphics.rect(drawX, drawY, coll.width, coll.height);
            this.debugGraphics.fill({ color: 0x0064ff, alpha: 0.2 });
            this.debugGraphics.stroke({ color: 0x0064ff, alpha: 0.9, width: 2 });
          }
        }
      }

      // Draw object bounds (yellow) for reference
      if (obj.width && obj.height) {
        if (obj.rotation && obj.rotation !== 0) {
          const angle = (obj.rotation * Math.PI) / 180;
          const hw = obj.width / 2;
          const hh = obj.height / 2;
          const cos = Math.cos(angle);
          const sin = Math.sin(angle);
          
          const corners = [
            { x: -hw, y: -hh },
            { x: hw, y: -hh },
            { x: hw, y: hh },
            { x: -hw, y: hh }
          ];

          const points = [];
          for (const c of corners) {
            points.push(
              obj.x + c.x * cos - c.y * sin,
              obj.y + c.x * sin + c.y * cos
            );
          }

          this.debugGraphics.poly(points);
          this.debugGraphics.stroke({ color: 0xffff00, alpha: 0.4, width: 1 });
        } else {
          this.debugGraphics.rect(
            obj.x - obj.width / 2, 
            obj.y - obj.height / 2, 
            obj.width, 
            obj.height
          );
          this.debugGraphics.stroke({ color: 0xffff00, alpha: 0.4, width: 1 });
        }
      }
    }
  },

  /**
   * Reset renderer state without destroying containers
   * Called when returning to lobby to clear cached sprites
   */
  reset() {
    // Clear object sprites (world objects like chests, pickups, etc.)
    for (const sprite of this.objectSprites.values()) {
      if (sprite.parent) {
        sprite.parent.removeChild(sprite);
      }
      sprite.destroy();
    }
    this.objectSprites.clear();

    // Clear grid, arena fill and walls so they redraw on next game
    if (this.groundGraphics) {
      this.groundGraphics.clear();
    }
    if (this.gridGraphics) {
      this.gridGraphics.clear();
    }
    if (this.wallsGraphics) {
      this.wallsGraphics.clear();
    }
    if (this.debugGraphics) {
      this.debugGraphics.clear();
    }
    if (this.zoneOverlayGraphics) {
      this.zoneOverlayGraphics.clear();
    }
    if (this.zoneBorderGraphics) {
      this.zoneBorderGraphics.clear();
    }
  },

  /**
   * Clean up resources
   */
  destroy() {
    // Clean up tile sprites
    for (const sprite of this.tileSprites) {
      if (sprite.parent) {
        sprite.parent.removeChild(sprite);
      }
      sprite.destroy();
    }
    this.tileSprites = [];
    this.tileTextures.clear();

    // Clean up object sprites
    for (const sprite of this.objectSprites.values()) {
      sprite.destroy();
    }
    this.objectSprites.clear();

    if (this.groundGraphics) {
      this.groundGraphics.destroy();
    }
    if (this.gridGraphics) {
      this.gridGraphics.destroy();
    }
    if (this.wallsGraphics) {
      this.wallsGraphics.destroy();
    }
    if (this.debugGraphics) {
      this.debugGraphics.destroy();
    }
    if (this.zoneOverlayGraphics) {
      this.zoneOverlayGraphics.destroy();
    }
    if (this.zoneBorderGraphics) {
      this.zoneBorderGraphics.destroy();
    }
  },

  /**
   * Update the zone overlay graphics
   * Creates a danger zone overlay OUTSIDE the safe zone
   * SQUARE VERSION - Much more performant than circle (just 4 rectangles, no textures)
   */
  updateZoneOverlay(zoneState, mapWidth, mapHeight) {
    if (!this.zoneBorderGraphics) return;

    // Clear previous frame
    this.zoneBorderGraphics.clear();
    if (this.zoneOverlayGraphics) {
      this.zoneOverlayGraphics.clear();
    }

    if (!zoneState || !zoneState.shape) {
      return;
    }

    const width = mapWidth || 0;
    const height = mapHeight || 0;
    const padding = 2000; // How far outside the map to extend the danger zone

    // Zone bounds: square zones as-is, a circle as its bounding square.
    // Shared with the minimap so both draw the same rectangle.
    const bounds = zoneBounds(zoneState.shape);
    if (!bounds) return;
    const { minX: safeLeft, minY: safeTop, maxX: safeRight, maxY: safeBottom } = bounds;

    const safeWidth = safeRight - safeLeft;
    const safeHeight = safeBottom - safeTop;

    // Draw 4 rectangles around the safe zone (the danger zone overlay)
    // This is extremely performant - just 4 simple rects, no textures or blend modes
    this.zoneOverlayGraphics.alpha = 1;

    // Top rectangle (above safe zone)
    this.zoneOverlayGraphics.rect(-padding, -padding, width + padding * 2, safeTop + padding);
    // Bottom rectangle (below safe zone)
    this.zoneOverlayGraphics.rect(-padding, safeBottom, width + padding * 2, height - safeBottom + padding);
    // Left rectangle (left of safe zone, between top and bottom)
    this.zoneOverlayGraphics.rect(-padding, safeTop, safeLeft + padding, safeHeight);
    // Right rectangle (right of safe zone, between top and bottom)
    this.zoneOverlayGraphics.rect(safeRight, safeTop, width - safeRight + padding, safeHeight);

    // Fill all rectangles with danger zone color
    this.zoneOverlayGraphics.fill({ color: 0x1e3d2d, alpha: 0.45 });

    // Draw border around safe zone
    this.zoneBorderGraphics.alpha = 0.9;
    this.zoneBorderGraphics.setStrokeStyle({ width: 6, color: 0x72ffb8 });
    this.zoneBorderGraphics.rect(safeLeft, safeTop, safeWidth, safeHeight);
    this.zoneBorderGraphics.stroke();


  },

  /**
   * Helper to draw serialized zone shapes
   * @private
   */
  _drawZoneShapePath(graphics, shape) {
    if (!shape) return;

    if (shape.type === 'circle' && shape.center) {
      graphics.circle(shape.center.x, shape.center.y, shape.radius || 0);
    } else if (shape.type === 'rect' && shape.bounds) {
      const { minX = 0, minY = 0, maxX = 0, maxY = 0 } = shape.bounds;
      graphics.rect(minX, minY, (maxX - minX), (maxY - minY));
    }
  },

  /**
   * Helper to draw serialized zone shapes in reverse winding order
   * Used for creating cutouts with evenodd fill rule
   * @private
   */
  _drawZoneShapePathReverse(graphics, shape) {
    if (!shape) return;

    if (shape.type === 'circle' && shape.center) {
      // Draw circle using arc with counter-clockwise direction
      const x = shape.center.x;
      const y = shape.center.y;
      const radius = shape.radius || 0;

      // Use arc to draw counter-clockwise
      graphics.arc(x, y, radius, 0, Math.PI * 2, true);
    } else if (shape.type === 'rect' && shape.bounds) {
      const { minX = 0, minY = 0, maxX = 0, maxY = 0 } = shape.bounds;
      // Draw rectangle in reverse order (clockwise instead of counter-clockwise)
      graphics.moveTo(minX, minY);
      graphics.lineTo(minX, maxY);
      graphics.lineTo(maxX, maxY);
      graphics.lineTo(maxX, minY);
      graphics.lineTo(minX, minY);
    }
  }
};

// Export for use in other modules

export { pixiWorldLoggerFallback, pixiWorldLogger, PixiWorldRenderer };
